// The docks' harbour (docs/164 "The docks' look", docs/166 C2): setts on the land, planks on a pier,
// shingle on rough ground, and the joins `dock_board.py.txt` draws from the set's `harbour_tones`: a
// coping where setts meet water, `wall_face_quay` on the water square south of stone and
// `wall_face_piles` south of timber, a sill where setts meet planks, a timber edge where planks meet
// water, shadows on the water, and the shingle's bank as the file gives it (no foam of the painter's own).
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';
import type { Canvas, RGBA } from './canvas';
import { C, type Ctx } from './context';
import { groundOf, type Ground } from './ground';

export const isDocks = (ctx: Ctx): boolean => ctx.has('harbour_tones') && ctx.has('wall_face_quay');

const liquid = (ctx: Ctx, x: number, y: number): boolean => { const s = ctx.sq.at(x, y); return s === 'water' || s === 'deep'; };

/** A pier: floor at most three squares across with water at both ends of the run, across or down (the forge places no jetty yet, docs/166 E2). */
function pier(ctx: Ctx, x: number, y: number): boolean {
  const open = (a: number, b: number): boolean => { const s = ctx.sq.at(a, b); return s === 'floor' || s === 'rough'; };
  for (const [dx, dy] of [[1, 0], [0, 1]] as const) {
    let a = 0, b = 0;
    while (a < 3 && open(x - dx * (a + 1), y - dy * (a + 1))) a++;
    while (b < 3 && open(x + dx * (b + 1), y + dy * (b + 1))) b++;
    if (a + b + 1 <= 3 && liquid(ctx, x - dx * (a + 1), y - dy * (a + 1)) && liquid(ctx, x + dx * (b + 1), y + dy * (b + 1))) return true;
  }
  return false;
}

/** Setts on land, planks on a pier, shingle on rough ground. */
export function docksGroundOf(ctx: Ctx, x: number, y: number, s: string, g: Ground | undefined): Ground | undefined {
  if (s === 'rough') return groundOf(ctx, 'rough', 'floor_shingle', x, y) ?? g;
  if (s !== 'floor' && s !== 'door' && s !== 'way') return g;
  if (pier(ctx, x, y)) return groundOf(ctx, 'floor_planks', 'floor', x, y) ?? g;
  return groundOf(ctx, 'floor', 'floor_patch', x, y) ?? g;
}

type Kind = 'c' | 'p' | 's' | 'w' | undefined;

/** The harbour's joins, in place of the shared banks. */
export function docksBanks(ctx: Ctx, out: Canvas, grounds: ReadonlyArray<Ground | undefined>): void {
  const { sq } = ctx, { w, h } = sq;
  const toneP = ctx.all('harbour_tones')[0]!;
  const tone = (i: number): RGBA => { const k = i * 4; return [toneP.px[k]!, toneP.px[k + 1]!, toneP.px[k + 2]!, 255]; };
  const kind = (x: number, y: number): Kind => {
    if (!sq.on(x, y)) return undefined;
    const s = sq.at(x, y);
    if (s === 'water' || s === 'deep') return 'w';
    if (s === 'wall' || s === 'rock') return 'c';
    const g = grounds[y * w + x];
    if (!g) return undefined;
    return g.name === 'floor_planks' ? 'p' : g.name === 'rough' ? 's' : 'c';
  };
  const paved = (x: number, y: number): boolean => { const s = sq.at(x, y); return kind(x, y) === 'c' && s !== 'wall' && s !== 'rock'; };
  const [FOAM, FOAM2, WETS] = [tone(8), tone(9), tone(10)];
  const SIDES = [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]] as const;
  // the shingle's bank on the water square: the shingle reaching in, a dark wet line, then the file's two colours
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (kind(x, y) !== 'w') continue;
    for (const [s, dx, dy] of SIDES) {
      if (kind(x + dx, y + dy) !== 's') continue;
      const n = grounds[(y + dy) * w + x + dx]!, prof = EDGE_DEPTHS[ctx.roll(`sh:${s}`, x, y) % EDGE_DEPTHS.length]!;
      for (let i = 0; i < C; i++) {
        const d0 = prof[i]!;
        for (let k = 0; k < d0 + 2; k++) {
          const [ix, iy] = s === 'n' ? [i, k] : s === 's' ? [i, C - 1 - k] : s === 'w' ? [k, i] : [C - 1 - k, i];
          if (k < d0) out.copy(x * C + ix, y * C + iy, n.piece, ix, iy, n.turn);
          else out.set(x * C + ix, y * C + iy, k === d0 ? WETS : ctx.roll('f', x * C + i, y) % 3 ? FOAM : FOAM2);
        }
      }
    }
  }
  // shadows on the water: from stone or timber to the west, a quay's to the north, a deck's deeper
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (kind(x, y) !== 'w') continue;
    const X = x * C, Y = y * C, west = kind(x - 1, y), north = kind(x, y - 1);
    if (west === 'c' || west === 'p') out.rect(X, Y, X + 3, Y + C - 1, [8, 28, 38, 95]);
    if (north === 'c') out.rect(X, Y + 12, X + C - 1, Y + 17, [8, 28, 38, 95]);
    if (north === 'p') out.rect(X, Y, X + C - 1, Y + 10, [8, 24, 32, 150]);
  }
  // the coping along setts beside water, and the quay's wall on the water square to the south
  const COPE = [tone(0), tone(1)];
  const wet = (x: number, y: number): boolean => kind(x, y) === 'w';
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!paved(x, y)) continue;
    const X = x * C, Y = y * C;
    for (let j = 0; j < C; j++) {
      if (wet(x - 1, y)) { out.set(X, Y + j, COPE[1]!); out.set(X + 1, Y + j, COPE[0]!); }
      if (wet(x + 1, y)) { out.set(X + C - 2, Y + j, COPE[0]!); out.set(X + C - 1, Y + j, tone(11)); }
      if (wet(x, y - 1)) { out.set(X + j, Y, COPE[1]!); out.set(X + j, Y + 1, COPE[0]!); }
      if (wet(x, y + 1)) { out.set(X + j, Y + C - 2, COPE[0]!); out.set(X + j, Y + C - 1, COPE[1]!); }
    }
  }
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (wet(x, y) && kind(x, y - 1) === 'c') { const q = ctx.pick('wall_face_quay', 'quay', x, y); if (q) out.draw(q, x * C, y * C); }
  }
  // a stone sill where setts meet planks, a timber edge and piles where planks meet water
  const SILL = [tone(2), tone(3), tone(4)], BEAM = [tone(5), tone(6), tone(7)];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (kind(x, y) !== 'p') continue;
    const X = x * C, Y = y * C;
    if (paved(x, y - 1)) for (let i = 0; i < C; i++) SILL.forEach((c, k) => out.set(X + i, Y + k, c));
    if (paved(x - 1, y) || paved(x + 1, y)) {
      const west = paved(x - 1, y), ox = west ? X : X + C - 3, cs = west ? SILL : [...SILL].reverse();
      for (let j = 0; j < C; j++) cs.forEach((c, k) => out.set(ox + k, Y + j, c));
    }
    for (let j = 0; j < C; j++) {
      if (wet(x - 1, y)) { out.set(X, Y + j, BEAM[2]!); out.set(X + 1, Y + j, BEAM[0]!); }
      if (wet(x + 1, y)) { out.set(X + C - 2, Y + j, BEAM[1]!); out.set(X + C - 1, Y + j, BEAM[2]!); }
      if (wet(x, y - 1)) { out.set(X + j, Y, BEAM[2]!); out.set(X + j, Y + 1, BEAM[0]!); }
    }
    if (wet(x, y + 1)) {
      for (let i = 0; i < C; i++) out.set(X + i, Y + C - 1, BEAM[0]!);   // the deck's front timber runs on to the water square below
      const p = ctx.pick('wall_face_piles', 'piles', x, y);
      if (p) out.draw(p, X, Y + C);
    }
  }
}
